import React, { FC, Fragment, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Badge,
  Button,
  CardBody,
  Col,
  FormGroup,
  FormText,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { OrderRefund, RefundResponse } from '../../../../shared/orders';
import { Order, RefundStatus } from '../../../../shared/orders/model';
import {
  Filter,
  FilterTable,
  MaterialIcon,
  PageHeader,
  RefundPaymentTable,
  refundUpdatePreSubmit,
  RichMarkdownIt,
  UserStateComponent,
} from '../../../components';
import { JSONForm } from '../../../components/json-form/JSONForm';
import { getOrderItem, getRefund } from '../../../models';
import { launder, useTranslation } from '../../../translations';
import { capitalize, Fetcher, sum, useFetcher, useToggle } from '../../../utils';
import { OrderItemProduct, OrderRefundItem } from '../../cart';
import { RefundInfo } from './refundInfo';

type RefundApprovalFormData = Record<string, number>;

interface RefundApprovalPostData {
  paymentData: Record<number, number>;
  revoke?: boolean;
  reason: string;
  customerMessage?: string | null;
}

interface RefundApprovalResponse {
  success: boolean;
  amountRefunded: number;
}

const filters: Filter<OrderRefund>[] = [
  {
    displayName: 'common.status',
    name: 'status',
    options: ['pending', 'approved', 'denied'].map((type) => {
      return {
        filter: (request) => request.status === type,
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        name: launder(capitalize(type)),
        value: type,
      };
    }),
  },
];

const DigitalGrantInput: FC<{
  readonly order: Order;
  readonly toggleRevokingGrants: () => void;
}> = ({ order, toggleRevokingGrants }) => {
  const { ts } = useTranslation();
  if (order.orderItems.every((t) => !t.referenceId)) {
    return (
      <FormGroup>
        <div className="mt-10" id="noRevoke">
          <Input name="revoke" type="hidden" value="false" />
          <b>{ts('attendee.refund.note_this_order_does_not')}</b>
        </div>
      </FormGroup>
    );
  }

  return (
    <FormGroup style={{ marginLeft: '7px' }}>
      <FormGroup check className="mt-10">
        <Input
          defaultChecked
          id="revoke"
          name="revoke"
          onChange={toggleRevokingGrants}
          type="checkbox"
        />
        <Label check for="revoke">
          {ts('attendee.refund.revoke_digital_grants')}
        </Label>
      </FormGroup>
      <FormText color="muted">{ts('attendee.refund.if_you_want_to_refund')}</FormText>
    </FormGroup>
  );
};

const StockReturnInput: FC<{
  readonly revokingGrant: boolean;
  readonly order: Order;
  readonly products: RefundResponse['products'];
}> = ({ revokingGrant, order, products }) => {
  const { ts } = useTranslation();
  if (
    !revokingGrant ||
    order.orderItems.every((t) => !products.find((p) => p.id === t.product.id)?.hasLimit)
  ) {
    return null;
  }

  return (
    <FormGroup style={{ marginLeft: '7px' }}>
      <FormGroup check className="mt-10">
        <Input defaultChecked id="stockReturn" name="stockReturn" type="checkbox" />
        <Label check for="stockReturn">
          {ts('attendee.refund.return_item_to_available_stock')}
        </Label>
      </FormGroup>
      <FormText color="muted">{ts('common.if_you_dont_want_to')}</FormText>
    </FormGroup>
  );
};

const RejectModal: FC<{
  readonly request: OrderRefund;
  readonly refresh: () => void;
  readonly dismissModal: () => void;
}> = ({ request, refresh, dismissModal }) => {
  const { ts } = useTranslation();
  return (
    <Modal className="modal-large" id="rejectModal" isOpen>
      <JSONForm
        method="post"
        onSuccess={() => {
          toast.success(ts('attendee.refund.refund_request_rejected', request.id));
          refresh();
          dismissModal();
        }}
        path={`/api/refunds/${request.id}/reject`}
      >
        <ModalHeader>
          {ts('common.refund', request.id)} &mdash; {ts('common.reject')}
        </ModalHeader>
        <ModalBody>
          <p>{ts('attendee.refund.your_reason_for_rejecting_this')}</p>
          <p>{ts('attendee.refund.you_may_optionally_provide_a')}</p>
          <FormGroup row>
            <Label for="reason" lg={5} sm={12}>
              {ts('attendee.refund.reason_for_rejection')}
              <span className="text-danger">*</span>
            </Label>
            <Col lg={7} sm={12}>
              <Input id="reason" name="reason" required type="textarea" />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="customerMessage" lg={5} sm={12}>
              {ts('attendee.refund.message_to_customer')}
            </Label>
            <Col lg={7} sm={12}>
              <Input id="customerMessage" name="customerMessage" type="textarea" />
            </Col>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              dismissModal();
            }}
          >
            {ts('common.cancel')}
          </Button>{' '}
          <Button color="danger" id="submitRejectRefund" type="submit">
            {ts('attendee.refund.reject_refund')}
          </Button>
        </ModalFooter>
      </JSONForm>
    </Modal>
  );
};

const ApproveModal: FC<{
  readonly request: OrderRefund;
  readonly products: RefundResponse['products'];
  readonly refresh: () => void;
  readonly dismissModal: () => void;
}> = ({ request, products, refresh, dismissModal }) => {
  const { currency } = useTranslation();
  const { order } = request;
  const refund = getRefund(request.order, request.id);
  const totalToRefund = sum(
    refund.items.map((item) => getOrderItem(request.order, item.itemId).price),
  );

  const [revokingGrant, toggleRevokingGrant] = useToggle(true);

  const { ts } = useTranslation();
  return (
    <Modal className="modal-large" id="approveModal" isOpen>
      <JSONForm<RefundApprovalResponse, RefundApprovalFormData & RefundApprovalPostData>
        method="post"
        onSuccess={(data) => {
          if (data.success) {
            toast.success(ts('attendee.refund.refund_request_approved', request.id));
          } else {
            toast.warn(
              ts(
                'attendee.refund.refund_request_requestid_was_only',
                request.id,
                currency(data.amountRefunded),
              ),
              { autoClose: false },
            );
          }

          refresh();
          dismissModal();
        }}
        path={`/api/refunds/${request.id}/approve`}
        preSubmit={refundUpdatePreSubmit}
      >
        <ModalHeader>
          {ts('common.refund', request.id)} &mdash; {ts('common.approve')}
        </ModalHeader>
        <ModalBody>
          <RichMarkdownIt source={ts('common.use_the_table_below_to')} />
          <h4>{ts('attendee.refund.products_to_be_refunded')}</h4>
          <hr />
          {refund.items.map((item) => {
            return (
              <Fragment key={item.itemId}>
                <OrderItemProduct
                  item={getOrderItem(request.order, item.itemId)}
                  order={order}
                  readOnly
                />
                <OrderRefundItem orderItems={order.orderItems} refund={refund} />
              </Fragment>
            );
          })}
          <h4>{ts('common.payments')}</h4>
          <RefundPaymentTable payments={order.payments} totalToRefund={totalToRefund} />
          <DigitalGrantInput order={order} toggleRevokingGrants={toggleRevokingGrant} />
          <StockReturnInput order={order} products={products} revokingGrant={revokingGrant} />
          <FormGroup row>
            <Label for="reason" lg={5} sm={12}>
              {ts('common.reason_for_approval')}
              <span className="text-danger">*</span>
            </Label>
            <Col lg={7} sm={12}>
              <Input id="reason" name="reason" required type="textarea" />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="customerMessage" lg={5} sm={12}>
              {ts('attendee.refund.message_to_customer')}
            </Label>
            <Col lg={7} sm={12}>
              <Input id="customerMessage" name="customerMessage" type="textarea" />
            </Col>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              dismissModal();
            }}
          >
            {ts('common.cancel')}
          </Button>{' '}
          <Button color="success" id="submitApproveRefund" type="submit">
            {ts('attendee.refund.approve_refund')}
          </Button>
        </ModalFooter>
      </JSONForm>
    </Modal>
  );
};

const RefundElement: FC<{ readonly request: OrderRefund }> = ({ request }) => {
  const refund = getRefund(request.order, request.id);
  const { ts } = useTranslation();
  return (
    <div className="table-item" id={`refundRequest${request.id}`}>
      <p>
        <strong>
          {ts('common.refund', request.id)} &mdash; {ts('common.order')} #{request.order.id}
        </strong>{' '}
        <RenderElementBadges status={request.status} />
      </p>
      <hr />
      <br />
      {refund.items.map((item) => (
        <OrderItemProduct
          item={getOrderItem(request.order, item.itemId)}
          key={item.itemId}
          order={request.order}
          readOnly
        />
      ))}
    </div>
  );
};

const RefundInfoElement: FC<{
  readonly products: RefundResponse['products'];
  readonly request?: OrderRefund;
  readonly refresh: () => void;
}> = ({ request, products, refresh }) => {
  const { ts } = useTranslation();
  const [modal, setModal] = useState<'approve' | 'reject' | false>(false);

  if (!request) {
    return <CardBody>{ts('attendee.refund.select_request_lef_side')}</CardBody>;
  }

  return (
    <>
      <RefundInfo
        onAction={(action) => {
          setModal(action);
        }}
        request={request}
      />
      {modal === 'reject' && (
        <RejectModal
          dismissModal={() => {
            setModal(false);
          }}
          refresh={refresh}
          request={request}
        />
      )}
      {modal === 'approve' && (
        <ApproveModal
          dismissModal={() => {
            setModal(false);
          }}
          products={products}
          refresh={refresh}
          request={request}
        />
      )}
    </>
  );
};

export const PendingRefunds: FC = () => {
  const { ts } = useTranslation();
  const fetcher = useFetcher(async () => {
    return await api.getRefunds();
  });

  if (!fetcher.success) {
    return <Fetcher result={fetcher} />;
  }

  const { refunds, products } = fetcher.data;

  return (
    <UserStateComponent>
      <PageHeader>{ts('common.refund_requests')}</PageHeader>
      <Row className="justify-content-center" id="refundList">
        {refunds.length > 0 && (
          <Col xs={12}>
            <FilterTable<OrderRefund>
              data={refunds}
              filters={filters}
              infoRenderer={(d) => (
                <RefundInfoElement products={products} refresh={fetcher.refresh} request={d} />
              )}
              renderer={(d) => <RefundElement request={d} />}
            />
          </Col>
        )}
        {refunds.length === 0 && (
          <Col className="text-center" lg={8} xs={12}>
            <MaterialIcon large name="sentiment_satisfied" />
            <h4>{ts('attendee.refund.there_are_currently_no_pending')}</h4>
            <p>{ts('attendee.refund.when_a_user_requests_a')}</p>
          </Col>
        )}
      </Row>
    </UserStateComponent>
  );
};

const RenderElementBadges: FC<{ readonly status: RefundStatus }> = ({ status }) => {
  const { ts } = useTranslation();
  switch (status) {
    case 'pending': {
      return <Badge color="warning">{ts('common.pending')}</Badge>;
    }

    case 'approved': {
      return <Badge color="success">{ts('common.approved')}</Badge>;
    }

    case 'denied': {
      return <Badge color="danger">{ts('common.denied')}</Badge>;
    }
  }
};
